import React, { useMemo } from 'react'
import isEmpty from 'lodash/isEmpty'
import SuccessIcon from 'transfix-icons/build/SuccessIcon'
import WarningFilledIcon from 'transfix-icons/build/WarningFilledIcon'
import { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { ICarrierApplicationStates } from 'types/FMSGraphqlTypes'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'

const useCarrierComplianceTextStyles = makeStyles(theme => ({
  carrierComplianceRow: {
    padding: theme.spacing(1),
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
  warning: {
    color: brandColors.warning,
  },
  info: {
    color: brandColors.info,
  },
  missingRequirements: {
    paddingLeft: theme.spacing(6),
  },
}))

export const CarrierComplianceText = () => {
  const { carrierCompliance } = useMarketplaceContext()
  const { requirementsMatchResults = [], state } = carrierCompliance || {}
  const classes = useCarrierComplianceTextStyles()

  const missingRequirements = requirementsMatchResults.filter(requirement => !requirement.valid)

  const {
    Icon: CarrierStatusIcon,
    text: carrierStatusText,
    variant: carrierStatusVariant,
  } = useMemo(() => {
    if (state === ICarrierApplicationStates.activated) {
      return {
        Icon: SuccessIcon,
        variant: INLINE_MESSAGE_VARIANTS.INFO,
        text: 'Approved to haul Transfix loads',
      }
    }
    if (state === ICarrierApplicationStates.expired_insurance) {
      return {
        Icon: WarningFilledIcon,
        variant: INLINE_MESSAGE_VARIANTS.WARNING,
        text: 'Information is needed to activate your carrier',
      }
    }

    return {
      Icon: WarningFilledIcon,
      variant: INLINE_MESSAGE_VARIANTS.WARNING,
      text: 'Not approved to haul Transfix loads',
    }
  }, [state])

  return (
    <Box data-test='carrier-compliance-text'>
      <Box my={1}>
        <Typography variant='subtitle2'>
          We need to conduct a compliance check before you can book this load, based on the info we
          have about your carrier. If you submit a winning bid for this load instead, we’ll ask you
          to complete a few steps as part of our compliance check.
        </Typography>
      </Box>
      <Box mb={1}>
        <Typography variant='overline'>REQUIRED CRITERIA:</Typography>
      </Box>
      {carrierStatusText && (
        <Box display='flex' className={classes.carrierComplianceRow}>
          <Box className={classes.icon}>
            <CarrierStatusIcon
              size='large'
              className={`${
                carrierStatusVariant === INLINE_MESSAGE_VARIANTS.INFO
                  ? classes.info
                  : classes.warning
              } large`}
            />
          </Box>
          <Box>
            <Typography variant='subtitle2'>{carrierStatusText}</Typography>
          </Box>
        </Box>
      )}
      {!isEmpty(missingRequirements) && (
        <>
          <Box
            data-test='carrier-compliance-text-missing-requirements'
            display='flex'
            className={classes.carrierComplianceRow}>
            <Box className={classes.icon}>
              <WarningFilledIcon size='large' color='warning' />
            </Box>
            <Box>
              <Typography variant='subtitle2'>
                We need to verify that your carrier can comply with this load’s additional insurance
                requirements.
              </Typography>
            </Box>
          </Box>
          <Box>
            {missingRequirements.map(missingReq => (
              <Typography
                component='li'
                variant='subtitle2'
                key={missingReq.shipperRequirement}
                className={classes.missingRequirements}>
                {missingReq.shipperRequirement}
              </Typography>
            ))}
          </Box>
        </>
      )}
    </Box>
  )
}
